import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, HStack, Text, VStack } from "@chakra-ui/react";
import { FormTextarea } from "@/components/jobOffer/ui";
import { toaster } from "@/components/ui/toaster";
import { CandidateService } from "@/services/CandidateService";
import CandidateStatusBadge from "../CandidateStatusBadge";
import CandidateAxisPill from "../CandidateAxisPill";

// Mirrors RecruiterDecisionPolicy (API); the API is authoritative.
const DECISION_POLICY = {
    justificationForOverride: true,
    justificationForNegative: true,
    justificationForReanalysis: false,
};
const NEGATIVE_STATUSES = ["Rejected"];
const FINAL_STATUSES = ["QualifiedForNextStage", "ReadyToHire", "Hired", "Rejected"];
const CHOICES = ["QualifiedForNextStage", "FurtherSelection", "ReadyToHire", "Hired", "Rejected"];
const RECOMMENDED = { positive: "QualifiedForNextStage", conditional: "FurtherSelection", negative: "Rejected" };

const CandidateDecisionSection = ({ candidate, onDecided }) => {
    const { t } = useTranslation("models");
    const [mode, setMode] = useState(null); // accept | override | reanalyze
    const [status, setStatus] = useState(null);
    const [justification, setJustification] = useState("");
    const [note, setNote] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    const decided = !!candidate.humanDecision && candidate.humanDecision.toLowerCase() !== "undecided";
    const recommended = RECOMMENDED[(candidate.aiRecommendation || "").toLowerCase()] ?? null;
    const target = mode === "accept" ? recommended : mode === "override" ? status : null;
    const needsReason = mode === "reanalyze"
        ? DECISION_POLICY.justificationForReanalysis
        : !!target && (
            FINAL_STATUSES.includes(target)
            || (DECISION_POLICY.justificationForOverride && mode === "override" && target !== recommended)
            || (DECISION_POLICY.justificationForNegative && NEGATIVE_STATUSES.includes(target))
        );
    const canSubmit = !!mode && (mode === "reanalyze" || !!target) && (!needsReason || !!justification.trim());

    const open = (m) => { setMode(m); setStatus(null); setJustification(""); setNote(""); };

    const submit = async () => {
        setIsLoading(true);
        try {
            const type = { accept: "AcceptRecommendation", override: "Override", reanalyze: "Reanalyze" }[mode];
            const updated = await CandidateService.recordDecision(candidate.id, {
                type, status: mode === "override" ? status : null,
                justification: justification.trim() || null, note: note.trim() || null,
            });
            toaster.create({ type: "success", title: t("candidateDecision.saved") });
            setMode(null);
            onDecided(updated);
        } catch (e) {
            toaster.create({ type: "error", title: e?.response?.data?.error ?? t("candidateDetailPage.statusUpdateError") });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Box w="full" bg="white" border="1px solid" borderColor="gray.200" borderRadius={12} p={5}>
            <VStack align="stretch" gap={3}>
                <HStack gap={3} flexWrap="wrap">
                    <Text fontSize="md" fontWeight={700} color="gray.900">{t("candidateDecision.title")}</Text>
                </HStack>
                <HStack gap={4} flexWrap="wrap">
                    <HStack gap={2}>
                        <Text fontSize="xs" color="gray.500">{t("candidateDecision.aiLabel")}:</Text>
                        <CandidateAxisPill axis="aiRecommendation" value={candidate.aiRecommendation} />
                    </HStack>
                    <HStack gap={2}>
                        <Text fontSize="xs" color="gray.500">{t("candidateDecision.humanLabel")}:</Text>
                        <CandidateAxisPill axis="humanDecision" value={candidate.humanDecision} />
                    </HStack>
                </HStack>
                <Text fontSize="xs" color="gray.500">{t("candidateDecision.hint")}</Text>
                {!decided && <HStack gap={2} flexWrap="wrap">
                    <Button size="sm" variant={mode === "accept" ? "primary" : "outline"} disabled={!recommended} onClick={() => open("accept")}>
                        {t("candidateDecision.accept")}
                    </Button>
                    <Button size="sm" variant={mode === "override" ? "primary" : "outline"} onClick={() => open("override")}>
                        {t("candidateDecision.override")}
                    </Button>
                    <Button size="sm" variant={mode === "reanalyze" ? "primary" : "outline"} onClick={() => open("reanalyze")}>
                        {t("candidateDecision.reanalyze")}
                    </Button>
                </HStack>}
                {!decided && mode && (
                    <VStack align="stretch" gap={3}>
                        {mode === "accept" && <HStack gap={2}><Text fontSize="sm" color="gray.700">{t("candidateDecision.willSet")}</Text><CandidateStatusBadge status={recommended} /></HStack>}
                        {mode === "override" && (
                            <HStack gap={2} flexWrap="wrap">
                                {CHOICES.map(s => (
                                    <Box
                                        key={s}
                                        as="button"
                                        type="button"
                                        onClick={() => setStatus(s)}
                                        px={2}
                                        py={1}
                                        borderRadius={8}
                                        border="1px solid"
                                        borderColor={status === s ? "purple.400" : "gray.200"}
                                        bg={status === s ? "purple.50" : "gray.50"}
                                        cursor="pointer"
                                    >
                                        <CandidateStatusBadge status={s} />
                                    </Box>
                                ))}
                            </HStack>
                        )}
                        {mode === "reanalyze" && <Text fontSize="sm" color="gray.700">{t("candidateDecision.reanalyzeInfo")}</Text>}
                        <Text fontSize="sm" color="gray.500">
                            {t(needsReason ? "candidateDecision.justificationRequired" : "candidateDecision.justification")}
                        </Text>
                        <FormTextarea rows={3} resize="vertical" value={justification} onChange={(e) => setJustification(e.target.value)} />
                        <Text fontSize="sm" color="gray.500">{t("candidateDecision.note")}</Text>
                        <FormTextarea rows={2} resize="vertical" value={note} onChange={(e) => setNote(e.target.value)} />
                        <HStack justify="flex-end" gap={2}>
                            <Button variant="ghost" size="sm" color="gray.600" onClick={() => setMode(null)} disabled={isLoading}>
                                {t("candidateDetailPage.changeStatusModalCancel")}
                            </Button>
                            <Button variant="primary" size="sm" loading={isLoading} disabled={!canSubmit} onClick={submit}>
                                {t("candidateDecision.confirm")}
                            </Button>
                        </HStack>
                    </VStack>
                )}
            </VStack>
        </Box>
    );
};

export default CandidateDecisionSection;
